import React from 'react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', cyan:'#00FFFF', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)' };

const Section = ({ title, children }) => (
  <div style={s.section}>
    <h2 style={s.h2}>{title}</h2>
    {children}
  </div>
);

export default function Terms() {
  return (
    <div style={s.root}>
      <MarketingNav />

      <div style={s.hero}>
        <div style={s.heroBg} />
        <div style={s.eyebrow}>Legal</div>
        <h1 style={s.h1}>Terms of Service</h1>
        <p style={s.heroSub}>Last updated: 20 March 2026</p>
      </div>

      <div style={s.body}>
        <div style={s.inner}>

          <Section title="1. Agreement">
            <p style={s.p}>These Terms of Service ("Terms") govern your access to and use of SenseCritiq ("Service"), operated by Salvis Are, Grünauer Str. 68, 12524 Berlin, Germany ("we", "us"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>
          </Section>

          <Section title="2. Description of Service">
            <p style={s.p}>SenseCritiq is a UX research synthesis platform. It accepts research artifacts (audio, video, transcripts), processes them through an AI pipeline, and produces structured insights: themes, findings, quotes, and reports. The Service is accessible via the web portal at sensecritiq.com and through an MCP server for use with AI assistants such as Claude.</p>
          </Section>

          <Section title="3. Accounts and API Keys">
            <p style={s.p}>You are responsible for maintaining the security of your account credentials and API keys. Do not share API keys or commit them to version control. You are responsible for all activity that occurs under your account. Notify us immediately at <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a> if you suspect unauthorised access.</p>
            <p style={s.p}>You must be at least 18 years old and have the authority to bind yourself or your organisation to these Terms. Accounts are not transferable.</p>
          </Section>

          <Section title="4. Acceptable Use">
            <p style={s.p}>You may use the Service for lawful research and analytical purposes. You must not:</p>
            <ul style={s.ul}>
              <li style={s.li}>Upload content you do not have the right to process (e.g. recordings made without participant consent)</li>
              <li style={s.li}>Attempt to reverse-engineer, scrape, or circumvent usage limits</li>
              <li style={s.li}>Use the Service to process content that is illegal, defamatory, or violates third-party rights</li>
              <li style={s.li}>Resell or sublicense access to the Service without our written consent</li>
              <li style={s.li}>Upload content that contains malware or harmful code</li>
            </ul>
            <p style={s.p}>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
          </Section>

          <Section title="5. Research Participant Consent">
            <p style={s.p}>You are solely responsible for obtaining all necessary consents from research participants before uploading recordings or transcripts. We act as a data processor on your instructions; you are the data controller responsible for establishing a lawful basis under applicable privacy law (e.g. GDPR).</p>
          </Section>

          <Section title="6. Subscriptions and Billing">
            <p style={s.p}>Subscriptions are billed monthly in EUR via Stripe. Plans auto-renew unless cancelled before the renewal date. You may upgrade, downgrade, or cancel at any time from the billing page — cancellations take effect at the end of the current billing period.</p>
            <p style={s.p}><strong style={s.strong}>Quota.</strong> Sessions per month reset on your billing date. Unused sessions do not roll over.</p>
            <p style={s.p}><strong style={s.strong}>Refunds.</strong> We offer a full refund within 14 days of your first paid subscription if you are not satisfied — this is in addition to any statutory withdrawal rights under EU consumer law. After 14 days, refunds are at our discretion. Contact <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a> to request one.</p>
            <p style={s.p}><strong style={s.strong}>Price changes.</strong> We will give at least 30 days' notice before increasing prices.</p>
          </Section>

          <Section title="7. Your Content">
            <p style={s.p}>You retain full ownership of all files and content you upload. By uploading, you grant us a limited licence to process your content solely to deliver the Service. We do not use your research content to train AI models.</p>
            <p style={s.p}>Upon account deletion or 30 days after cancellation, your content is permanently deleted from our systems and those of our sub-processors.</p>
          </Section>

          <Section title="8. Intellectual Property">
            <p style={s.p}>The Service, including its software, design, and branding, is owned by Salvis Are trading as SenseCritiq. Nothing in these Terms grants you any rights to our intellectual property other than the limited right to use the Service as described here.</p>
          </Section>

          <Section title="9. Availability">
            <p style={s.p}>We aim for high availability but do not guarantee uninterrupted service. Planned maintenance will be communicated in advance where possible. We do not guarantee processing time for async pipeline jobs.</p>
          </Section>

          <Section title="10. Disclaimers">
            <p style={s.p}>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. AI-GENERATED SYNTHESIS IS AN AID FOR RESEARCHERS — NOT A SUBSTITUTE FOR PROFESSIONAL JUDGEMENT. WE DO NOT WARRANT THAT OUTPUTS WILL BE ACCURATE OR COMPLETE.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p style={s.p}>To the maximum extent permitted by German law, our liability for indirect, incidental, or consequential damages is excluded. Our total liability for any claim shall not exceed the amount you paid us in the 3 months prior to the claim.</p>
            <p style={s.p}>Nothing in these Terms limits liability for intentional misconduct, gross negligence, or claims under the German Product Liability Act (ProdHaftG).</p>
          </Section>

          <Section title="12. Governing Law">
            <p style={s.p}>These Terms are governed by the laws of Germany. Disputes shall be subject to the exclusive jurisdiction of the courts of Berlin, unless mandatory consumer protection law provides otherwise.</p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p style={s.p}>We may update these Terms from time to time. We will notify you by email at least 14 days before material changes take effect. Continued use after the effective date constitutes acceptance.</p>
          </Section>

          <Section title="Contact">
            <p style={s.p}>Questions about these Terms: <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a></p>
          </Section>

        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },
  hero: { textAlign:'center', padding:'80px 24px 60px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(28px,4vw,48px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:14, position:'relative' },
  heroSub: { fontSize:14, color:C.muted2, position:'relative' },
  body: { padding:'0 24px 80px' },
  inner: { maxWidth:1100, margin:'0 auto' },
  section: { borderTop:`1px solid ${C.border}`, paddingTop:36, marginTop:36 },
  h2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:20, fontWeight:700, letterSpacing:'-0.02em', marginBottom:16, color:C.white },
  p: { fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:14 },
  ul: { paddingLeft:20, margin:'0 0 14px' },
  li: { fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:8 },
  strong: { color:C.white, fontWeight:600 },
  link: { color:C.cyan, textDecoration:'none' },
};
